import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin } from 'lucide-react';
import { settingsApi } from '@/services/api';

const CONTENT = {
  ar: {
    title: 'سياسة الخصوصية',
    company: 'شركة أرمن للمطابخ',
    intro: [
      'تلتزم شركة أرمن للمطابخ بحماية خصوصية عملائها وزوار موقعها الإلكتروني، ونحرص على توضيح كيفية جمع واستخدام وحماية البيانات الشخصية عند استخدام الموقع أو التواصل معنا أو طلب خدماتنا.',
      'باستخدامك لموقعنا الإلكتروني أو تقديم بياناتك لنا، فإنك توافق على سياسة الخصوصية الموضحة أدناه.',
    ],
    sections: [
      {
        title: 'أولًا: البيانات التي نقوم بجمعها',
        paras: ['قد نقوم بجمع بعض البيانات عند استخدامك للموقع أو تعبئة النماذج أو التواصل معنا، وتشمل:'],
        list: [
          'الاسم الكامل.',
          'رقم الجوال.',
          'البريد الإلكتروني.',
          'المدينة أو المنطقة.',
          'تفاصيل الطلب أو الاستفسار.',
          'نوع المطبخ المطلوب أو مساحة المشروع.',
          'الصور أو المخططات التي يرسلها العميل لغرض التصميم أو المعاينة.',
          'بيانات التصفح مثل الصفحات التي تمت زيارتها ومدة التصفح، عند استخدام أدوات التحليل والإعلانات.',
        ],
      },
      {
        title: 'ثانيًا: الغرض من جمع البيانات',
        paras: ['نستخدم البيانات التي يتم تزويدنا بها للأغراض التالية:'],
        list: [
          'التواصل مع العميل والرد على الاستفسارات.',
          'تقديم الاستشارات والعروض السعرية.',
          'إعداد التصاميم والمقترحات المناسبة لاحتياج العميل.',
          'متابعة الطلبات والمشاريع وخدمات ما بعد البيع.',
          'تحسين تجربة المستخدم في الموقع.',
          'إرسال العروض والتحديثات التسويقية عند موافقة العميل.',
          'تحليل أداء الحملات الإعلانية وتحسين الخدمات.',
          'الالتزام بأي متطلبات نظامية أو تنظيمية ذات علاقة.',
        ],
      },
      {
        title: 'ثالثًا: مشاركة البيانات',
        paras: [
          'لا تقوم شركة أرمن للمطابخ ببيع أو تأجير بيانات العملاء لأي طرف آخر.',
          'قد تتم مشاركة بعض البيانات عند الحاجة مع جهات مساندة لتقديم الخدمة، مثل:',
        ],
        list: [
          'فرق التوصيل أو التركيب.',
          'مزودي خدمات الدفع الإلكتروني.',
          'مزودي خدمات الاستضافة والتقنية.',
          'أدوات التحليل والإعلانات.',
          'الجهات الرسمية عند وجود طلب نظامي أو التزام قانوني.',
        ],
        after: ['ويتم ذلك في حدود الغرض المطلوب وبما يحافظ على سرية البيانات.'],
      },
      {
        title: 'رابعًا: ملفات تعريف الارتباط',
        paras: [
          'قد يستخدم موقعنا ملفات تعريف الارتباط وتقنيات مشابهة لتحسين تجربة التصفح، وتحليل أداء الموقع، وقياس فعالية الحملات الإعلانية.',
          'يمكن للمستخدم التحكم في إعدادات ملفات تعريف الارتباط من خلال إعدادات المتصفح، مع العلم أن تعطيلها قد يؤثر على بعض خصائص الموقع.',
        ],
      },
      {
        title: 'خامسًا: حماية البيانات',
        paras: [
          'نحرص في شركة أرمن للمطابخ على اتخاذ الإجراءات المناسبة لحماية البيانات الشخصية من الوصول غير المصرح به أو الاستخدام غير النظامي أو الفقدان أو التعديل.',
          'ومع ذلك، فإن نقل البيانات عبر الإنترنت لا يمكن ضمان أمانه بشكل كامل، لذلك نوصي بعدم إرسال أي معلومات حساسة غير مطلوبة.',
        ],
      },
      {
        title: 'سادسًا: مدة الاحتفاظ بالبيانات',
        paras: [
          'نحتفظ بالبيانات الشخصية للمدة اللازمة لتحقيق الأغراض التي جُمعت من أجلها، مثل متابعة الطلبات، تقديم الخدمات، خدمات ما بعد البيع، أو الالتزام بالمتطلبات النظامية والمحاسبية.',
          'وعند انتفاء الحاجة إلى البيانات، يتم حذفها أو إخفاء هويتها متى كان ذلك ممكنًا.',
        ],
      },
      {
        title: 'سابعًا: حقوق العميل',
        paras: ['يحق للعميل طلب ما يلي:'],
        list: [
          'معرفة البيانات الشخصية التي نحتفظ بها عنه.',
          'طلب تصحيح أو تحديث البيانات غير الدقيقة.',
          'طلب حذف البيانات عند عدم الحاجة إليها، متى كان ذلك ممكنًا.',
          'طلب إيقاف استخدام البيانات لأغراض تسويقية.',
          'الاستفسار عن طريقة استخدام بياناته الشخصية.',
        ],
        after: ['ويمكن للعميل ممارسة هذه الحقوق من خلال التواصل معنا عبر وسائل التواصل المعتمدة.'],
      },
      {
        title: 'ثامنًا: الرسائل التسويقية',
        paras: [
          'قد نستخدم بيانات التواصل لإرسال العروض أو التحديثات أو الرسائل التسويقية الخاصة بخدمات أرمن للمطابخ، وذلك عند موافقة العميل أو وجود علاقة قائمة معه.',
          'يمكن للعميل طلب إيقاف الرسائل التسويقية في أي وقت من خلال التواصل معنا.',
        ],
      },
      {
        title: 'تاسعًا: الروابط الخارجية',
        paras: [
          'قد يحتوي موقعنا على روابط لمواقع أو منصات خارجية. لا تتحمل شركة أرمن للمطابخ مسؤولية سياسات الخصوصية أو محتوى تلك المواقع، وننصح بمراجعة سياسة الخصوصية الخاصة بكل موقع يتم الانتقال إليه.',
        ],
      },
      {
        title: 'عاشرًا: تحديث سياسة الخصوصية',
        paras: [
          'قد تقوم شركة أرمن للمطابخ بتحديث سياسة الخصوصية من وقت لآخر بما يتوافق مع تطوير خدماتها أو المتطلبات النظامية.',
          'سيتم نشر أي تحديث على هذه الصفحة، ويعد استمرار استخدام الموقع بعد التحديث موافقة على السياسة المعدلة.',
        ],
      },
    ],
    contactTitle: 'الحادي عشر: التواصل معنا',
    contactIntro:
      'لأي استفسار بخصوص سياسة الخصوصية أو طريقة استخدام البيانات الشخصية، يمكنكم التواصل مع شركة أرمن للمطابخ عبر:',
    emailLabel: 'البريد الإلكتروني',
    phoneLabel: 'رقم الجوال / الواتساب',
    addressLabel: 'العنوان',
    closingName: 'شركة أرمن للمطابخ',
    closingText: 'نحترم خصوصيتكم ونسعى دائمًا لحماية بياناتكم.',
  },
  en: {
    title: 'Privacy Policy',
    company: 'Arman Kitchens Company',
    intro: [
      'Arman Kitchens Company is committed to protecting the privacy of its customers and website visitors. We are keen to explain how personal data is collected, used, and protected when you use the website, contact us, or request our services.',
      'By using our website or providing us with your data, you agree to the privacy policy set out below.',
    ],
    sections: [
      {
        title: '1. Data We Collect',
        paras: ['We may collect certain data when you use the website, fill out forms, or contact us, including:'],
        list: [
          'Full name.',
          'Mobile number.',
          'Email address.',
          'City or region.',
          'Details of the request or inquiry.',
          'The type of kitchen required or the project area.',
          'Photos or plans sent by the customer for design or site-inspection purposes.',
          'Browsing data such as pages visited and browsing duration, when analytics and advertising tools are used.',
        ],
      },
      {
        title: '2. Purpose of Data Collection',
        paras: ['We use the data provided to us for the following purposes:'],
        list: [
          'Communicating with the customer and responding to inquiries.',
          'Providing consultations and price quotations.',
          'Preparing designs and proposals suited to the customer’s needs.',
          'Following up on orders, projects, and after-sales services.',
          'Improving the user experience on the website.',
          'Sending offers and marketing updates with the customer’s consent.',
          'Analyzing advertising campaign performance and improving services.',
          'Complying with any relevant statutory or regulatory requirements.',
        ],
      },
      {
        title: '3. Data Sharing',
        paras: [
          'Arman Kitchens Company does not sell or rent customer data to any other party.',
          'Some data may be shared, when necessary, with supporting parties in order to deliver the service, such as:',
        ],
        list: [
          'Delivery or installation teams.',
          'Electronic payment service providers.',
          'Hosting and technical service providers.',
          'Analytics and advertising tools.',
          'Official authorities in the event of a statutory request or legal obligation.',
        ],
        after: ['This is done within the limits of the required purpose and in a manner that preserves data confidentiality.'],
      },
      {
        title: '4. Cookies',
        paras: [
          'Our website may use cookies and similar technologies to improve the browsing experience, analyze website performance, and measure the effectiveness of advertising campaigns.',
          'Users can control cookie settings through their browser settings, noting that disabling them may affect some website features.',
        ],
      },
      {
        title: '5. Data Protection',
        paras: [
          'At Arman Kitchens Company, we take appropriate measures to protect personal data from unauthorized access, unlawful use, loss, or alteration.',
          'However, the security of data transmitted over the internet cannot be fully guaranteed, so we recommend not sending any sensitive information that is not required.',
        ],
      },
      {
        title: '6. Data Retention Period',
        paras: [
          'We retain personal data for as long as necessary to fulfill the purposes for which it was collected, such as following up on orders, providing services, after-sales services, or complying with statutory and accounting requirements.',
          'When the data is no longer needed, it is deleted or anonymized whenever possible.',
        ],
      },
      {
        title: '7. Customer Rights',
        paras: ['The customer has the right to request the following:'],
        list: [
          'To know what personal data we hold about them.',
          'To have inaccurate data corrected or updated.',
          'To have their data deleted when it is no longer needed, whenever possible.',
          'To stop the use of their data for marketing purposes.',
          'To inquire about how their personal data is used.',
        ],
        after: ['Customers can exercise these rights by contacting us through our approved communication channels.'],
      },
      {
        title: '8. Marketing Messages',
        paras: [
          'We may use contact data to send offers, updates, or marketing messages related to Arman Kitchens services, with the customer’s consent or where an existing relationship is in place.',
          'The customer may request to stop receiving marketing messages at any time by contacting us.',
        ],
      },
      {
        title: '9. External Links',
        paras: [
          'Our website may contain links to external websites or platforms. Arman Kitchens Company is not responsible for the privacy policies or content of those websites, and we recommend reviewing the privacy policy of every website you visit.',
        ],
      },
      {
        title: '10. Privacy Policy Updates',
        paras: [
          'Arman Kitchens Company may update this privacy policy from time to time in line with the development of its services or statutory requirements.',
          'Any update will be published on this page, and continued use of the website after the update constitutes acceptance of the amended policy.',
        ],
      },
    ],
    contactTitle: '11. Contact Us',
    contactIntro:
      'For any inquiries regarding the privacy policy or how personal data is used, you can contact Arman Kitchens Company via:',
    emailLabel: 'Email',
    phoneLabel: 'Mobile / WhatsApp',
    addressLabel: 'Address',
    closingName: 'Arman Kitchens Company',
    closingText: 'We respect your privacy and always strive to protect your data.',
  },
};

const PrivacyPolicyPage = () => {
  const { currentLang } = useLanguage();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    settingsApi.get().then((res) => setSettings(res.data)).catch(() => {});
  }, []);

  const isAr = currentLang === 'ar';
  const c = isAr ? CONTENT.ar : CONTENT.en;

  useEffect(() => {
    document.title = isAr ? 'سياسة الخصوصية | أرمن للمطابخ' : 'Privacy Policy | Arman Kitchens';
    window.scrollTo(0, 0);
  }, [isAr]);

  const s = settings || {};
  const email = s.email || 'info@armankitchens.com';
  const phone = '0593591595';
  const address = isAr ? 'الامام سعود بن عبد العزيز' : 'Imam Saud Bin Abdulaziz Road';

  return (
    <main data-testid="privacy-policy-page" className="pt-24">
      {/* Hero Banner */}
      <section className="bg-brand-dark text-white grain">
        <div className="container-custom relative z-10 py-20 md:py-28">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <span className="label-text mb-4 block">{c.company}</span>
            <h1 className="text-4xl md:text-5xl font-bold">{c.title}</h1>
          </motion.div>
        </div>
      </section>

      {/* Policy Content */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            {c.intro.map((p, i) => (
              <p key={i} className="text-muted-foreground text-lg leading-relaxed mb-4">
                {p}
              </p>
            ))}

            {c.sections.map((section, i) => (
              <div key={i} className="mt-12">
                <h2 className="text-2xl font-bold text-foreground mb-4">{section.title}</h2>
                {(section.paras || []).map((p, j) => (
                  <p key={j} className="text-muted-foreground leading-relaxed mb-3">
                    {p}
                  </p>
                ))}
                {section.list && (
                  <ul className="space-y-2 mb-3">
                    {section.list.map((item, j) => (
                      <li key={j} className="flex items-start gap-3 text-muted-foreground leading-relaxed">
                        <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {(section.after || []).map((p, j) => (
                  <p key={j} className="text-muted-foreground leading-relaxed mb-3">
                    {p}
                  </p>
                ))}
              </div>
            ))}

            {/* Contact Section */}
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">{c.contactTitle}</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">{c.contactIntro}</p>
              <ul className="space-y-4 bg-secondary p-6 md:p-8">
                <li className="flex items-start gap-3">
                  <Mail size={18} className="text-gold mt-1 flex-shrink-0" />
                  <span className="text-muted-foreground">
                    <strong className="text-foreground">{c.emailLabel}:</strong>{' '}
                    <a href={`mailto:${email}`} className="hover:text-gold transition-colors">
                      {email}
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Phone size={18} className="text-gold mt-1 flex-shrink-0" />
                  <span className="text-muted-foreground">
                    <strong className="text-foreground">{c.phoneLabel}:</strong>{' '}
                    <a href={`tel:${phone}`} className="hover:text-gold transition-colors" dir="ltr">
                      {phone}
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-gold mt-1 flex-shrink-0" />
                  <span className="text-muted-foreground">
                    <strong className="text-foreground">{c.addressLabel}:</strong> {address}
                  </span>
                </li>
              </ul>
            </div>

            {/* Closing */}
            <div className="mt-12 pt-8 border-t border-border text-center">
              <p className="text-foreground font-bold text-lg">{c.closingName}</p>
              <p className="text-muted-foreground mt-2">{c.closingText}</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicyPage;
